import { z } from 'zod';
import { formatInTimeZone } from 'date-fns-tz';

/** Fecha de hoy en America/Lima como YYYY-MM-DD. */
export function todayLima(): string {
  return formatInTimeZone(new Date(), 'America/Lima', 'yyyy-MM-dd');
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Edad cumplida (en años) de `birthDate` a la fecha `today`; ambas YYYY-MM-DD. */
export function yearsBetween(birthDate: string, today: string): number {
  const [by, bm, bd] = birthDate.split('-').map(Number);
  const [ty, tm, td] = today.split('-').map(Number);
  let years = ty - by;
  if (tm < bm || (tm === bm && td < bd)) years -= 1;
  return years;
}

/** Schema del registro público, parametrizado por la fecha de hoy (para tests deterministas). */
export function makeApplicationSchema(today: string) {
  return z
    .object({
      documentType: z.enum(['DNI', 'CE'], {
        errorMap: () => ({ message: 'Selecciona DNI o CE.' }),
      }),
      dni: z.string().trim(),
      firstNames: z.string().trim().min(1, 'Ingresa tus nombres.'),
      lastNames: z.string().trim().min(1, 'Ingresa tus apellidos.'),
      /**
       * Se normaliza ANTES de validar: era el único campo que exigía el formato exacto sobre el
       * texto crudo. Un número pegado desde WhatsApp («+51 987 654 321») o con un espacio al final
       * se rechazaba con un mensaje que no explicaba nada, y el postulante no veía qué corregir.
       */
      phone: z
        .string()
        .trim()
        .transform((v) => v.replace(/[\s.()-]/g, '').replace(/^\+?51/, ''))
        .pipe(z.string().regex(/^9\d{8}$/, 'El celular debe tener 9 dígitos y empezar en 9.')),
      email: z.string().trim().email('Ingresa un correo válido.'),
      birthDate: z
        .string()
        .regex(ISO_DATE, 'Ingresa tu fecha de nacimiento (AAAA-MM-DD).')
        .refine((d) => yearsBetween(d, today) >= 18, 'Debes ser mayor de 18 años para registrarte.'),
      department: z.string().trim().min(1, 'Selecciona tu departamento.'),
      province: z.string().trim().min(1, 'Selecciona tu provincia.'),
      district: z.string().trim().min(1, 'Selecciona tu distrito.'),
      address: z.string().trim(),
      bank: z.string().trim(),
      accountNumber: z.string().trim(),
      // Nota: `z.union([z.literal(''), ...regex], { errorMap })` NO aplica su errorMap en
      // zod 3.23.8 (la unión propaga el `invalid_string` de la rama regex con el mensaje
      // por defecto "Invalid"). Un refine sobre el string da el mensaje exacto del contrato.
      cci: z
        .string()
        .trim()
        .refine(
          (v) => v === '' || /^\d{20}$/.test(v),
          'El CCI debe tener 20 dígitos (o déjalo vacío).',
        ),
    })
    .superRefine((v, ctx) => {
      if (v.documentType === 'DNI' && !/^\d{8}$/.test(v.dni)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dni'],
          message: 'El DNI debe tener 8 dígitos.',
        });
      }
      if (v.documentType === 'CE' && !/^[A-Za-z0-9]{9,12}$/.test(v.dni)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dni'],
          message: 'El carné de extranjería debe tener entre 9 y 12 caracteres alfanuméricos.',
        });
      }
    });
}

export const applicationSchema = makeApplicationSchema(todayLima());

export type ApplicationInput = z.infer<ReturnType<typeof makeApplicationSchema>>;

/** Documento laboral adjunto en el registro (se guarda en `worker_applications.documents` jsonb). */
export interface WorkerDocumentInput {
  type: 'carnet_sanidad' | 'certificado_unico_laboral' | 'otro';
  path: string;
  issue_date: string;
  expiry_date: string;
}

/** Payload completo que recibe `submitApplication` (datos + rutas ya subidas a Storage). */
export interface SubmitApplicationInput extends ApplicationInput {
  photoPath: string;
  docFrontPath: string;
  docBackPath: string;
  documents: WorkerDocumentInput[];
}
